import { Shield, Award, ScrollText, Compass } from "lucide-react";
import Reveal from "@/components/Reveal";

const BADGES = [
  { icon: Shield, k: "Integrity", v: "Every transaction, every time." },
  { icon: ScrollText, k: "Compliance", v: "CIN · UDYAM · GST · MCA verified." },
  { icon: Compass, k: "Growth", v: "Backing your dreams with capital." },
  { icon: Award, k: "Trust", v: "Built on the Sahyog promise." },
];

export default function BrandMark() {
  return (
    <section
      id="brand-mark"
      data-testid="brand-mark"
      className="relative bg-[#04102B] text-white py-24 md:py-32 overflow-hidden noise-overlay"
    >
      {/* Decorative arcs */}
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full border border-[#D4AF37]/10" />
      <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full border border-[#D4AF37]/10" />
      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[1100px] rounded-full border border-[#D4AF37]/[0.05]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="text-center">
            <span className="gold-divider text-[11px] tracking-[0.32em] uppercase">
              Trust &amp; Heritage
            </span>
            <h2
              data-testid="brand-mark-heading"
              className="font-display text-[40px] md:text-[58px] mt-5 leading-[1.05]"
            >
              The <em className="not-italic text-[#D4AF37]">Sahyog</em> emblem
            </h2>
            <p className="mt-4 text-white/70 max-w-2xl mx-auto">
              An identity forged from Indian finance heritage — combining growth, infinity and the
              promise of partnership.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-12 gap-12 items-center">
          {/* Emblem */}
          <Reveal className="lg:col-span-6 flex justify-center" delay={80}>
            <div className="relative">
              <div
                className="absolute inset-0 rounded-full blur-3xl"
                style={{
                  background:
                    "radial-gradient(circle, rgba(212,175,55,0.35), rgba(212,175,55,0.05) 60%, transparent 80%)",
                }}
              />
              <img
                data-testid="brand-emblem-img"
                src="/brand/sahyog-emblem-md.png"
                alt="Sahyog Finance & Noble Infinity Asset Management Pvt Ltd emblem"
                className="relative w-full max-w-[520px] mx-auto drop-shadow-[0_25px_70px_rgba(212,175,55,0.35)] float-slow"
              />
            </div>
          </Reveal>

          {/* Symbolism + values */}
          <div className="lg:col-span-6">
            <Reveal delay={140}>
              <div className="space-y-5">
                <div
                  data-testid="brand-symbol-crown"
                  className="rounded-xl p-5 border border-[#D4AF37]/25 bg-white/[0.04]"
                >
                  <div className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]">
                    The Crown
                  </div>
                  <div className="mt-1 text-white/85 text-sm leading-relaxed">
                    Sovereignty of trust — a promise that every customer is treated with the
                    dignity of royalty.
                  </div>
                </div>
                <div
                  data-testid="brand-symbol-shield"
                  className="rounded-xl p-5 border border-[#D4AF37]/25 bg-white/[0.04]"
                >
                  <div className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]">
                    The Shield · Growth + Infinity
                  </div>
                  <div className="mt-1 text-white/85 text-sm leading-relaxed">
                    A rising chart on one side, an infinity loop on the other — the duality of
                    Sahyog Finance (lending) and Noble Infinity (asset management).
                  </div>
                </div>
                <div
                  data-testid="brand-symbol-handshake"
                  className="rounded-xl p-5 border border-[#D4AF37]/25 bg-white/[0.04]"
                >
                  <div className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]">
                    The Handshake
                  </div>
                  <div className="mt-1 text-white/85 text-sm leading-relaxed">
                    Partnership over transactions — the cornerstone of our customer and associate
                    relationships.
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Core value badges */}
        <Reveal delay={200}>
          <div className="mt-16 grid grid-cols-1 min-[400px]:grid-cols-2 md:grid-cols-4 gap-4">
            {BADGES.map(({ icon: Icon, k, v }) => (
              <div
                key={k}
                data-testid={`brand-value-${k.toLowerCase()}`}
                className="rounded-2xl p-5 border border-[#D4AF37]/25 bg-gradient-to-b from-white/[0.04] to-transparent hover:border-[#D4AF37]/70 transition-all"
              >
                <span className="w-10 h-10 rounded-md grid place-items-center bg-[#D4AF37]/15 text-[#F0D788]">
                  <Icon size={18} />
                </span>
                <div className="mt-3 font-display text-lg sm:text-xl text-white break-words">{k}</div>
                <div className="mt-1 text-white/55 text-xs leading-relaxed">{v}</div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Tagline strip */}
        <Reveal delay={280}>
          <div
            data-testid="brand-tagline-strip"
            className="mt-14 flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-[#F0D788] uppercase tracking-[0.3em] text-sm md:text-base font-display"
          >
            <span>Building Wealth</span>
            <span className="text-[#D4AF37]">·</span>
            <span>Creating Value</span>
            <span className="text-[#D4AF37]">·</span>
            <span>Securing Your Future</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
